import { APP_INITIALIZER, Injectable, InjectionToken, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ClientsComponent } from './pages/clients/clients.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { RulesComponent } from './pages/rules/rules.component';
import { SettingsComponent } from './pages/settings/settings/settings.component';
import { SourcesComponent } from './pages/sources/sources.component';
import { UsersComponent } from './pages/users/users.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { SignInLayoutComponent } from './layouts/sign-in-layout/sign-in-layout.component';
import { GoHomeComponent } from './layouts/go-home.component';
import { StyleComponentsLibsModule } from '@helpers/shared/style-components-libs.module';
import {
	AlarisApiService,
	AlarisConfigService,
	AlarisErrorInterceptor,
	AlarisLanguageService,
	AlarisLocalStorageService,
	AlarisMultiOptions2Component,
	AlarisResponseInterceptor,
	AlarisTextAreaComponent,
	AUTH_SERVICE_INJECTOR,
	CONFIG_SERVICE_INJECTOR,
	ENVIRONMENT,
	ICONS_CONFIG,
	LANGUAGE_CONFIG,
	LANGUAGE_SERVICE_INJECTOR,
	LanguageConfig,
	LOCALSTORAGE_SERVICE_INJECTOR,
	MENU_MQ_SERVICE_INJECTOR,
	MENU_MQ_WIDTH_VALUE,
	MenuSidebarService,
	PROFILE_CONFIG,
	PROFILE_SERVICE_INJECTOR,
	ProfileConfig,
	China,
	English,
	Russian,
	Spain,
	AlarisRpcInterceptor,
	TAGGABLE_FIELD_CONFIG,
	AlarisLocalDateTransformerDirective,
	DATE_FILTER_END_OF_THE_DAY_INJECTOR,
	EDIT_PANEL_CONFIG,
	FILES_SERVICE_INJECTOR,
	AlarisFileLoaderComponent,
	DATE_FORMAT_SERVICE_INJECTOR,
	AlarisDateFormatService,
	provideAlarisToast
} from '@campaign-portal/components-library';
import { environment } from '../environments/environment';
import {
	MissingTranslationHandler,
	MissingTranslationHandlerParams,
	TranslateDefaultParser,
	TranslateLoader,
	TranslateModule,
	TranslateParser
} from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule } from '@angular/common/http';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeEs from '@angular/common/locales/es';
import localeEsExtra from '@angular/common/locales/extra/es';
import localeRu from '@angular/common/locales/ru';
import localeRuExtra from '@angular/common/locales/extra/ru';
import { RpcInterceptor } from '@helpers/interceptors/rpc.interceptor';
import { AuthService } from './auth/auth.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UserService } from './auth/user.service';
import { PasswordDialogComponent } from './pages/profile/password-dialog/password-dialog.component';
import { EmptyTableComponent } from '@helpers/components/empty-table/empty-table.component';
import { UsersDeleteDialogComponent } from './pages/users/dialog/dialog.component';
import { EditUserComponent } from './pages/users/edit-user/edit-user.component';
import { EditRuleComponent } from './pages/rules/edit-rule/edit-rule.component';
import { RulesDeleteDialogComponent } from './pages/rules/dialog/dialog.component';
import { SourcePipe } from './_helpers/repo/repo.pipe';
import { ClientsDeleteDialogComponent } from './pages/clients/dialog/dialog.component';
import { EditClientComponent } from './pages/clients/edit-client/edit-client.component';
import { SourcesDeleteDialogComponent } from './pages/sources/dialog/dialog.component';
import { EditSourceComponent } from './pages/sources/edit-source/edit-source.component';
import { ExportConfig } from '@helpers/interceptors/export-config';
import { ImportSettingsDialogComponent } from './pages/settings/settings/dialog/dialog.component';
import { STORAGE } from '@helpers/types/consts';
import { from, mergeMap, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DropAuthGuardService } from './auth/drop-auth.guard';
import { AuthGuardService } from './auth/auth.guard';
import { RolesComponent } from './pages/roles/roles.component';
import { RolesDeleteDialogComponent } from './pages/roles/dialog/dialog.component';
import { EditRoleComponent } from './pages/roles/edit-role/edit-role.component';
import { CurrenciesComponent } from './pages/settings/currencies/currencies.component';
import { CurrenciesDialogComponent } from './pages/settings/currencies/dialog/dialog.component';
import { RatesComponent } from './pages/settings/rates/rates.component';
import { RatesDialogComponent } from './pages/settings/rates/dialog/dialog.component';
import { EditCurrencyComponent } from './pages/settings/currencies/edit-currency/edit-currency.component';
import { EditRateComponent } from './pages/settings/rates/edit-rate/edit-rates.component';
import { DsIconComponent } from './_helpers/components/ds-icon/ds-icon.component';
import { ImportRatesComponent } from './pages/import rates/import-rates.component';
import { DeleteRatesDialogComponent } from './pages/import rates/delete-rates-dialog/delete-rates-dialog.component';
import { ImportRatesDialogComponent } from './pages/import rates/import-rates-dialog/import-rates-dialog.component';
import { ImportRatesFileService } from './pages/import rates/import-rates-dialog/import-rates-file.service';
import { ManualDippingComponent } from './pages/manual-dipping/manual-dipping.component';
import { CreateManualDippingComponent } from './pages/manual-dipping/create-manual-dipping/create-manual-dipping.component';
import { ManualDippingDeleteDialogComponent } from './pages/manual-dipping/dialog/dialog.component';
import { ManualDippingResultsComponent } from './pages/manual-dipping/manual-dipping-results/manual-dipping-results.component';
import { ManualDippingStatusComponent } from './pages/manual-dipping/manual-dipping-status/manual-dipping-status.component';
import { ManualDippingResultsTableComponent } from './pages/manual-dipping/manual-dipping-view-results/manual-dipping-results-table/manual-dipping-results-table.component';
import { ManualDippingViewResultsComponent } from './pages/manual-dipping/manual-dipping-view-results/manual-dipping-view-results.component';
import { ManualDippingErrorsTableComponent } from './pages/manual-dipping/manual-dipping-view-results/manual-dipping-errors-table/manual-dipping-errors-table.component';
import { ViewErrorLogComponent } from './pages/manual-dipping/manual-dipping-view-results/manual-dipping-errors-table/view-error-log/view-error-log.component';
import { HistoryLogsComponent } from './pages/history-logs/history-logs.component';
import { HistoryLogsChangesComponent } from './pages/history-logs/history-logs-changes/history-logs-changes.component';
import { SystemCodesDialogComponent } from './pages/response-code-translations/system-codes/dialog/dialog.component';
import { SystemCodesComponent } from './pages/response-code-translations/system-codes/system-codes.component';
import { EditSystemCodeComponent } from './pages/response-code-translations/system-codes/edit-code/edit-code.component';
import { TranslationRulesComponent } from './pages/response-code-translations/translation-rules/translation-rules.component';
import { EditTranslationRulesComponent } from './pages/response-code-translations/translation-rules/edit-rule/edit-rule.component';
import { TranslationRulesDialogComponent } from './pages/response-code-translations/translation-rules/dialog/dialog.component';
import { CircleWarningComponent } from './_helpers/components/circle-warning/circle-warning.component';
import { OVERLAY_DEFAULT_CONFIG, OverlayDefaultConfig } from '@angular/cdk/overlay';
import { SimulationComponent } from './pages/simulation/simulation.component';
import { RatesEditDialogComponentComponent } from './pages/settings/rates/rates-edit-dialog-component/rates-edit-dialog-component.component';
import { CreateSimulationComponent } from './pages/simulation/create-simulation/create-simulation.component';
import { SimulationResultsComponent } from './pages/simulation/simulation-results/simulation-results.component';
import { SimulationDeleteDialogComponent } from './pages/simulation/dialog/dialog.component';
import { SimulationStatusComponent } from './pages/simulation/simulation-status/simulation-status.component';

export const USERS_AND_ROLES_API = new InjectionToken<string>('UsersAndRolesAPIUrlPart');

/* Translate Module setup */
export function HttpLoaderFactory(http: HttpBackend): MultiTranslateHttpLoader {
	return new MultiTranslateHttpLoader(http, [
		{ prefix: './assets/i18n_lib/app.', suffix: '.json?' + environment.version },
		{ prefix: './assets/i18n/app.', suffix: '.json?' + environment.version }
	]);
}

export class MissingTranslationService implements MissingTranslationHandler {
	handle(params: MissingTranslationHandlerParams): string {
		console.warn(`WARN: '${params.key}' is missing in '${params.translateService.currentLang}' locale`);
		return params.key;
	}
}

@Injectable()
export class TranslateParserService extends TranslateDefaultParser {
	constructor() {
		super();
	}

	override getValue(target: any, key: string): any {
		const result = super.getValue(target, key);
		return typeof result === 'object' ? key : result;
	}
}

const translateConfig = {
	loader: {
		provide: TranslateLoader,
		useFactory: HttpLoaderFactory,
		deps: [HttpBackend]
	},
	missingTranslationHandler: {
		provide: MissingTranslationHandler,
		useClass: MissingTranslationService
	},
	parser: { provide: TranslateParser, useClass: TranslateParserService },
	// if we set `defaultLanguage` to 'en' right here - we force loading en.json with translations
	// so missing translations will be used from en.file
	defaultLanguage: ''
};

/* End of Translate Module setup */

/* Config */
const profileConfig: ProfileConfig = {
	profileRoute: '/profile',
	termsUrl: '',
	privacyUrl: '',
	supportEmail: ''
};

const userAndRolesApi = {
	url: ''
};

const iconsConfig = {
	baseUrl: '/assets/icons'
};

export const languageConfig: LanguageConfig = { languages: [English, Spain, Russian, China], currentLanguage: English };
export let eapiEnable = true;

export function loadConfig(
	appConfig: AlarisConfigService<{
		iconsBaseUlr?: string;
		showLogo?: boolean;
		showTitle?: boolean;
		isCore?: boolean;
	}>,
	store: AlarisLocalStorageService,
	translate: AlarisLanguageService,
	http: HttpClient
): () => Observable<any> {
	return (): Observable<any> =>
		from(appConfig.load()).pipe(
			mergeMap(() => {
				const reqTo = 'HlrSettings.ReadStartupParams';
				return http
					.post<any>(appConfig.api + '?' + reqTo, {
						method: reqTo
					})
					.pipe(
						map((res) => {
							const lang = store.get(STORAGE.LANGUAGE) ?? 'en';
							translateConfig.defaultLanguage = lang;
							registerLocaleData(localeEn, English.id, localeEnExtra);
							registerLocaleData(localeEs, Spain.id, localeEsExtra);
							registerLocaleData(localeRu, Russian.id, localeRuExtra);
							translate.set(lang);

							profileConfig.termsUrl = appConfig.termsUrl;
							profileConfig.privacyUrl = appConfig.privacyUrl;
							profileConfig.supportEmail = appConfig.supportEmail;

							userAndRolesApi.url = appConfig.api + 'usersroles/';
							iconsConfig.baseUrl = appConfig.env.iconsBaseUlr ?? '/assets/icons';

							// @ts-ignore
							if (!appConfig?.isCore && !res.data?.enableAddLang) {
								languageConfig.languages = [English, Spain, China];
							}
							eapiEnable = res.data.eapiEnable;

							environment.version = res.data.version + ' ' + res.data.release;
							if (res.data.buildDate) {
								const date = new Date(res.data.buildDate);

								const options: Intl.DateTimeFormatOptions = {
									day: 'numeric',
									month: 'long',
									year: 'numeric'
								};

								environment.releaseDate = new Intl.DateTimeFormat('en-US', options)
									.format(date)
									.toLowerCase();
							}
						})
					);
			})
		);
}

/* End of Config */

@NgModule({
	declarations: [
		AppComponent,
		ClientsComponent,
		ProfileComponent,
		RulesComponent,
		RolesComponent,
		RolesDeleteDialogComponent,
		SettingsComponent,
		CurrenciesComponent,
		CurrenciesDialogComponent,
		EditCurrencyComponent,
		EditRateComponent,
		RatesComponent,
		RatesEditDialogComponentComponent,
		RatesDialogComponent,
		HistoryLogsComponent,
		HistoryLogsChangesComponent,
		SourcesComponent,
		UsersComponent,
		MainLayoutComponent,
		SignInLayoutComponent,
		GoHomeComponent,
		PasswordDialogComponent,
		EmptyTableComponent,
		UsersDeleteDialogComponent,
		EditUserComponent,
		EditRuleComponent,
		EditRoleComponent,
		RulesDeleteDialogComponent,
		EditSystemCodeComponent,
		SystemCodesComponent,
		SystemCodesDialogComponent,
		TranslationRulesComponent,
		EditTranslationRulesComponent,
		TranslationRulesDialogComponent,
		SourcePipe,
		ClientsDeleteDialogComponent,
		EditClientComponent,
		SourcesDeleteDialogComponent,
		EditSourceComponent,
		ImportSettingsDialogComponent,
		ImportRatesComponent,
		ImportRatesDialogComponent,
		DeleteRatesDialogComponent,
		DsIconComponent,
		ManualDippingComponent,
		CreateManualDippingComponent,
		ManualDippingDeleteDialogComponent,
		ManualDippingResultsComponent,
		ManualDippingStatusComponent,
		ManualDippingResultsTableComponent,
		ManualDippingViewResultsComponent,
		ManualDippingErrorsTableComponent,
		SimulationComponent,
		CreateSimulationComponent,
		SimulationResultsComponent,
		SimulationDeleteDialogComponent,
		SimulationStatusComponent,
		ViewErrorLogComponent,
		CircleWarningComponent
	],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		HttpClientModule,
		FormsModule,
		ReactiveFormsModule,
		AppRoutingModule,
		TranslateModule.forRoot(translateConfig),
		StyleComponentsLibsModule,
		AlarisMultiOptions2Component,
		AlarisTextAreaComponent,
		AlarisLocalDateTransformerDirective,
		AlarisFileLoaderComponent
	],
	providers: [
		AlarisConfigService<{ iconsBaseUlr?: string }>,
		AlarisLocalStorageService,
		AlarisLanguageService,
		AlarisApiService,
		provideAlarisToast({ tapToDismiss: false, timeOut: 7000 }),

		{ provide: ENVIRONMENT, useValue: environment },
		{
			provide: APP_INITIALIZER,
			useFactory: loadConfig,
			deps: [AlarisConfigService, AlarisLocalStorageService, AlarisLanguageService, HttpClient],
			multi: true
		},
		ImportRatesFileService,
		{ provide: FILES_SERVICE_INJECTOR, useExisting: ImportRatesFileService },

		{ provide: LANGUAGE_CONFIG, useValue: languageConfig },

		{ provide: ICONS_CONFIG, useValue: iconsConfig },
		{ provide: PROFILE_CONFIG, useValue: profileConfig },

		{ provide: CONFIG_SERVICE_INJECTOR, useExisting: AlarisConfigService },
		{ provide: LOCALSTORAGE_SERVICE_INJECTOR, useExisting: AlarisLocalStorageService },
		{ provide: LANGUAGE_SERVICE_INJECTOR, useExisting: AlarisLanguageService },
		{ provide: AUTH_SERVICE_INJECTOR, useExisting: AuthService },
		{ provide: PROFILE_SERVICE_INJECTOR, useExisting: UserService },

		{ provide: DATE_FILTER_END_OF_THE_DAY_INJECTOR, useValue: true },

		{ provide: HTTP_INTERCEPTORS, useClass: RpcInterceptor, multi: true },

		{ provide: HTTP_INTERCEPTORS, useClass: AlarisRpcInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AlarisResponseInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AlarisErrorInterceptor, multi: true },

		{ provide: HTTP_INTERCEPTORS, useClass: ExportConfig, multi: true },

		{ provide: MENU_MQ_SERVICE_INJECTOR, useClass: MenuSidebarService },
		{ provide: MENU_MQ_WIDTH_VALUE, useValue: '(max-width: 1365px)' },

		{ provide: USERS_AND_ROLES_API, useValue: userAndRolesApi },
		AuthGuardService,
		DropAuthGuardService,
		{ provide: TAGGABLE_FIELD_CONFIG, useValue: { translation: false } },
		{ provide: EDIT_PANEL_CONFIG, useValue: { closeOnBackDropClick: false } },
		{ provide: DATE_FORMAT_SERVICE_INJECTOR, useClass: AlarisDateFormatService },
		DatePipe,
		{ provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } satisfies OverlayDefaultConfig }
	],
	bootstrap: [AppComponent]
})
export class AppModule {}
